import { useMemo, useState } from "react";
import { Copy, Check, Quote } from "lucide-react";
import { useLang } from "../context/LanguageContext";

/**
 * "Cite this article" widget for blog posts.
 *
 * Two-fold purpose:
 *  1. Reduces AI-slop plagiarism by giving crawlers (Perplexity, ChatGPT
 *     Search, Google AI Mode) a machine-readable, canonical citation string,
 *     which they now surface as the source when they attribute an answer.
 *  2. Encourages inbound links: researchers, students and journalists copy
 *     the citation into papers / articles, creating durable backlinks.
 */
export const CiteThisArticle = ({ title, author = "Velmira Living", date, url }) => {
  const { t } = useLang();
  const [copiedApa, setCopiedApa] = useState(false);
  const [copiedMla, setCopiedMla] = useState(false);

  const { apa, mla, year } = useMemo(() => {
    const d = date ? new Date(date) : new Date();
    const y = d.getFullYear();
    const mlaDate = d.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    return {
      year: y,
      apa: `${author} (${y}). ${title}. Velmira Living. ${url}`,
      mla: `${author}. "${title}." Velmira Living, ${mlaDate}, ${url}.`,
    };
  }, [title, author, date, url]);

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      if (which === "apa") { setCopiedApa(true); setTimeout(() => setCopiedApa(false), 1800); }
      else { setCopiedMla(true); setTimeout(() => setCopiedMla(false), 1800); }
    } catch {
      /* ignore */
    }
  };

  return (
    <section
      data-testid="cite-article"
      aria-labelledby="cite-article-title"
      className="mt-16 border border-border/60 bg-secondary p-7 md:p-8"
    >
      <div className="flex items-center gap-2 text-primary">
        <Quote size={16} strokeWidth={1.6} />
        <p
          id="cite-article-title"
          className="font-accent text-[11px] uppercase tracking-[0.25em]"
        >
          {t("blogUi.citeTitle")}
        </p>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* APA */}
        <div className="border border-border/60 bg-background p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="font-accent text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              {t("blogUi.citeApa")}
            </p>
            <button
              type="button"
              onClick={() => copy(apa, "apa")}
              data-testid="cite-copy-apa"
              className="inline-flex items-center gap-1.5 border border-primary/50 hover:border-primary text-primary px-3 py-1 uppercase tracking-[0.2em] text-[10px] font-body transition-colors"
            >
              {copiedApa ? <Check size={11} /> : <Copy size={11} />}
              {copiedApa ? t("blogUi.copied") : t("blogUi.copy")}
            </button>
          </div>
          <p className="font-body font-light text-sm text-foreground/85 leading-relaxed break-words">
            {apa}
          </p>
        </div>

        {/* MLA */}
        <div className="border border-border/60 bg-background p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="font-accent text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              {t("blogUi.citeMla")}
            </p>
            <button
              type="button"
              onClick={() => copy(mla, "mla")}
              data-testid="cite-copy-mla"
              className="inline-flex items-center gap-1.5 border border-primary/50 hover:border-primary text-primary px-3 py-1 uppercase tracking-[0.2em] text-[10px] font-body transition-colors"
            >
              {copiedMla ? <Check size={11} /> : <Copy size={11} />}
              {copiedMla ? t("blogUi.copied") : t("blogUi.copy")}
            </button>
          </div>
          <p className="font-body font-light text-sm text-foreground/85 leading-relaxed break-words">
            {mla}
          </p>
        </div>
      </div>

      <p className="mt-5 font-body text-xs text-foreground/50 leading-relaxed">
        {t("blogUi.citeNote").replace("{year}", String(year))}
      </p>
    </section>
  );
};

export default CiteThisArticle;
